import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import type { CSSProperties, PropsWithChildren } from "react";
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import "./SortableItem.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faGripVertical, faUpDownLeftRight } from "@fortawesome/free-solid-svg-icons";

interface Props {
  index: string;
  id: UniqueIdentifier;
  onMoveUp: () => void;
  onMoveDown: () => void;
  disableUp?: boolean;
  disableDown?: boolean;
}

interface Context {
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {}
});

export function SortableItem({
  children,
  index,
  id,
  onMoveUp,
  onMoveDown,
  disableUp = false,
  disableDown = false
}: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition
  } = useSortable({ id });

  const [isMovingUp, setIsMovingUp] = useState(false);
  const [isMovingDown, setIsMovingDown] = useState(false);

  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef
    }),
    [attributes, listeners, setActivatorNodeRef]
  );

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: transform ? CSS.Transform.toString(transform) : undefined, // Fix: Use CSS.Transform instead of CSS.Trandark
    transition
};

  const handleMoveUp = () => {
    setIsMovingUp(true);
    onMoveUp();
  };

  const handleMoveDown = () => {
    setIsMovingDown(true);
    onMoveDown();
  };

  useEffect(() => {
    if (isMovingUp || isMovingDown) {
      const timer = setTimeout(() => {
        setIsMovingUp(false);
        setIsMovingDown(false);
      }, 600); // Match the duration of your CSS transition

      return () => clearTimeout(timer);
    }
  }, [isMovingUp, isMovingDown]);

  return (
    <SortableItemContext.Provider value={context}>
      <li
        className={`SortableItem ${isMovingUp ? "moving-up" : ""} ${
          isMovingDown ? "moving-down" : ""
        }`}
        ref={setNodeRef}
        style={style}
      >
        <div className="button-group m-2 flex flex-col">
          <button
            onClick={handleMoveUp}
            disabled={disableUp}
            className="move-button text-sm text-gray-500 rounded-lg p-2"
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </button>
          <div className="text-xs text-center">{index}</div>
          <button
            onClick={handleMoveDown}
            disabled={disableDown}
            className="move-button text-sm text-gray-500 rounded-lg p-2"
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        </div>
        {children}
      </li>
    </SortableItemContext.Provider>
  );
}

export function DragHandle() {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <button
      className="flex p-2 items-center justify-center flex-shrink-0 touch-none cursor-[var(--cursor,_pointer)] border-none outline-none appearance-none bg-transparent tap-highlight-color-transparent text-gray-500 hover:bg-gray-100 rounded-lg"
      {...attributes}
      {...listeners}
      ref={ref}
    >
      <FontAwesomeIcon className="text-purple-500" icon={faUpDownLeftRight} scale={40}/>
    </button>
  );
}